/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `accessControl` for the Caintra Connect frontend.
 * @module utils/accessControl
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
/**
 * UI access helpers aligned with backend authorization
 * (`docs/backend.md`). The API still enforces these; this only hides
 * screens and avoids firing calls that will 403.
 */

import { ADMIN_USER_TYPE, NON_MEMBER_USER_TYPE } from "./eventDateFilters.js";
import { hasPermissionFlag } from "./pastEventsAccess.js";

export { ADMIN_USER_TYPE };
export { REPORTS_EXCLUDED_USER_TYPE } from "./pastEventsAccess.js";

/**
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export const isAdminUser = (user) =>
  String(user?.tipoDeUsuario ?? "") === ADMIN_USER_TYPE;

/**
 * Reports and buyer-company dumps: admin, or collaborator who is not type "13".
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export const canAccessReports = (user) => {
  if (!user) {
    return false;
  }
  if (isAdminUser(user)) {
    return true;
  }
  return (
    Boolean(user.esColaborador) && String(user.tipoDeUsuario ?? "") !== "13"
  );
};

/** Same privilege as Reports. */
export const canAccessBuyerCompanies = canAccessReports;

/**
 * Attendance lists / QR confirm: admin, or collaborator with viewEventAttendees.
 * @param {object|null|undefined} user
 * @param {object|Array|null|undefined} permissions
 * @returns {boolean}
 */
export const canAccessAttendance = (user, permissions) => {
  if (!user) {
    return false;
  }
  if (isAdminUser(user)) {
    return true;
  }
  return (
    Boolean(user.esColaborador) &&
    hasPermissionFlag(permissions, "viewEventAttendees")
  );
};

/**
 * @param {object|null|undefined} user
 * @param {object|Array|null|undefined} permissions
 * @param {string} permissionKey
 * @returns {boolean}
 */
export const canAccessCollaboratorPermission = (
  user,
  permissions,
  permissionKey,
) => {
  if (!user) {
    return false;
  }
  if (isAdminUser(user)) {
    return true;
  }
  return (
    Boolean(user.esColaborador) && hasPermissionFlag(permissions, permissionKey)
  );
};

export const canAccessCompanyCatalog = (user, permissions) =>
  canAccessCollaboratorPermission(user, permissions, "viewCompanyCatalog");

export const canAccessCategories = (user, permissions) =>
  canAccessCollaboratorPermission(user, permissions, "viewCategories");

export const canAccessUsers = (user, permissions) => {
  if (!user) {
    return false;
  }
  if (isAdminUser(user)) {
    return true;
  }
  return hasPermissionFlag(permissions, "viewUsers");
};

export const canAccessRoles = (user, permissions) =>
  canAccessCollaboratorPermission(user, permissions, "viewRoles");

export const canAccessCustomNotifications = (user, permissions) => {
  if (!user) {
    return false;
  }
  if (isAdminUser(user)) {
    return true;
  }
  return hasPermissionFlag(permissions, "viewCustomNotifications");
};

/**
 * Admin, or the given permission flag. Does not require `esColaborador`
 * (buyer roles can hold manage* flags).
 * @param {object|null|undefined} user
 * @param {object|Array|null|undefined} permissions
 * @param {string} permissionKey
 * @returns {boolean}
 */
export const canAccessByPermissionFlag = (user, permissions, permissionKey) => {
  if (!user) {
    return false;
  }
  if (isAdminUser(user)) {
    return true;
  }
  return hasPermissionFlag(permissions, permissionKey);
};

export const canManageBusinessEvents = (user, permissions) =>
  canAccessByPermissionFlag(user, permissions, "manageBusinessEvents");

export const canManageB2BEvents = (user, permissions) =>
  canAccessByPermissionFlag(user, permissions, "manageB2BEvents");

/** Executive buyer role for B2B virtual line self-service. */
const EXECUTIVE_BUYER_USER_TYPE = "11";

/**
 * B2B virtual line hub and queue: admin, manageB2BEvents, collaborators,
 * or executive buyers; not buyer type `"12"`.
 *
 * @param {object|null|undefined} user
 * @param {object|Array|null|undefined} permissions
 * @returns {boolean}
 */
export const canAccessB2BVirtualLineHub = (user, permissions) => {
  if (!user) {
    return false;
  }
  const userType = String(user.tipoDeUsuario ?? "");
  if (userType === EVENT_LIST_EXCLUDED_USER_TYPE) {
    return false;
  }
  if (userType === EXECUTIVE_BUYER_USER_TYPE) {
    return true;
  }
  if (isAdminUser(user) || hasPermissionFlag(permissions, "manageB2BEvents")) {
    return true;
  }
  return Boolean(user.esColaborador);
};

export const canManageVirtualEvents = (user, permissions) =>
  canAccessByPermissionFlag(user, permissions, "manageVirtualEvents");

export const canManageBreakfastEvents = (user, permissions) =>
  canAccessByPermissionFlag(user, permissions, "manageBreakfastEvents");

export const canAccessDirectory = (user, permissions) => {
  if (!user || String(user.tipoDeUsuario ?? "") === NON_MEMBER_USER_TYPE) {
    return false;
  }
  return canAccessByPermissionFlag(user, permissions, "viewDirectory");
};

export const canAccessRequirements = (user, permissions) =>
  canAccessByPermissionFlag(user, permissions, "viewRequirements");

export const canAccessEvents = (user, permissions) =>
  canAccessByPermissionFlag(user, permissions, "viewEvents");

export const canAccessChat = (user, permissions) =>
  canAccessByPermissionFlag(user, permissions, "viewChat");

/**
 * Homepage CMS (banners, partners, video tutorials): admin or editHomepage;
 * not No Socio (`"4"`) or buyer SSO (`"12"`).
 */
export const canEditHomepage = (user, permissions) => {
  if (!user) {
    return false;
  }
  const userType = String(user.tipoDeUsuario ?? "");
  if (userType === NON_MEMBER_USER_TYPE || userType === "12") {
    return false;
  }
  return canAccessByPermissionFlag(user, permissions, "editHomepage");
};

/**
 * Pause/cancel on the B2B virtual line queue (staff), not executive-buyer self-service.
 */
export const canManageB2BVirtualLineQueue = (user, permissions) => {
  if (!user) {
    return false;
  }
  const userType = String(user.tipoDeUsuario ?? "");
  if (userType === "12" || userType === EXECUTIVE_BUYER_USER_TYPE) {
    return false;
  }
  if (isAdminUser(user) || hasPermissionFlag(permissions, "manageB2BEvents")) {
    return true;
  }
  return Boolean(user.esColaborador);
};

/**
 * Registration-event management list. Matches the side menu:
 * admin, or (`manageEvents` or collaborator) and not type `"13"`.
 * @param {object|null|undefined} user
 * @param {object|Array|null|undefined} permissions
 * @returns {boolean}
 */
export const canManageRegistrationEvents = (user, permissions) => {
  if (!user) {
    return false;
  }
  if (isAdminUser(user)) {
    return true;
  }
  if (String(user.tipoDeUsuario ?? "") === "13") {
    return false;
  }
  return (
    hasPermissionFlag(permissions, "manageEvents") ||
    Boolean(user.esColaborador)
  );
};

/** Buyer role excluded from create/edit on event list pages. */
const EVENT_LIST_EXCLUDED_USER_TYPE = "12";

/**
 * Create/edit (and related management actions) on event list pages.
 * Combines the page's manage guard with the buyer-type exclusion used
 * across Personalizar event lists.
 *
 * @param {object|null|undefined} user
 * @param {object|Array|null|undefined} permissions
 * @param {(user: object|null|undefined, permissions: unknown) => boolean} canManagePage
 * @returns {boolean}
 */
export const canPerformEventListActions = (user, permissions, canManagePage) =>
  canManagePage(user, permissions) &&
  String(user?.tipoDeUsuario ?? "") !== EVENT_LIST_EXCLUDED_USER_TYPE;

/**
 * Staff-only B2B management (create/edit, participating-companies hub).
 * Executive buyers (`"11"`) may use the events list and their own schedule entry only.
 *
 * @param {object|null|undefined} user
 * @param {object|Array|null|undefined} permissions
 * @returns {boolean}
 */
export const canManageB2BEventStaffActions = (user, permissions) => {
  if (!user) {
    return false;
  }
  if (String(user?.tipoDeUsuario ?? "") === EXECUTIVE_BUYER_USER_TYPE) {
    return false;
  }
  return canPerformEventListActions(user, permissions, canManageB2BEvents);
};

/** Participating-companies virtual line hub (not executive-buyer self-service). */
export const canAccessB2BVirtualLineCompaniesHub = (user, permissions) =>
  canManageB2BEventStaffActions(user, permissions);
